import React, { useMemo, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const PRODUCT_LINES = [
  { key: "commercial_ppo", label: "Commercial PPO" },
  { key: "commercial_hmo", label: "Commercial HMO" },
  { key: "commercial_pos_epo", label: "Commercial POS / EPO" },
  { key: "medicare_advantage", label: "Medicare Advantage" },
  { key: "medicaid_managed_care", label: "Medicaid Managed Care" },
  { key: "marketplace_exchange", label: "Marketplace / Exchange" },
];

const emptyProductLines = () =>
  Object.fromEntries(PRODUCT_LINES.map((line) => [line.key, false]));

/**
 * CareVerify Manager attestation UX (PROMPT 6) — product-line checklist per payer.
 * Rejects payer-name-only submissions (at least one product line required).
 * Gated conceptually by careverify_manager (callable enforcement elsewhere).
 *
 * @param {{
 *   onSubmitAttestation?: (payload: { acceptedPayerPlans: Array }) => Promise<void>|void,
 *   disabled?: boolean,
 * }} props
 */
const FacilityAttestationPage = ({ onSubmitAttestation, disabled = false }) => {
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      payers: [{ payerName: "", productLines: emptyProductLines() }],
    },
  });

  const { fields, append } = useFieldArray({ control, name: "payers" });
  const payersWatch = watch("payers");

  const validationHint = useMemo(() => {
    if (!Array.isArray(payersWatch)) return null;
    for (const payer of payersWatch) {
      const name = (payer?.payerName || "").trim();
      const lines = payer?.productLines || {};
      const anyLine = PRODUCT_LINES.some((line) => lines[line.key] === true);
      if (name && !anyLine) {
        return "Select at least one product line for each payer — payer name alone is not allowed.";
      }
    }
    return null;
  }, [payersWatch]);

  const onSubmit = handleSubmit(async (values) => {
    setError("");
    setSuccess("");

    const acceptedPayerPlans = [];
    for (const payer of values.payers || []) {
      const payerName = (payer.payerName || "").trim();
      if (!payerName) continue;
      const selected = PRODUCT_LINES.filter((line) => payer.productLines?.[line.key]);
      if (selected.length === 0) {
        setError(
          "Each payer must include at least one product line. Payer-name-only attestation is not allowed.",
        );
        return;
      }
      const productLines = {};
      for (const line of PRODUCT_LINES) {
        if (payer.productLines?.[line.key]) {
          productLines[line.key] = "in";
        }
      }
      acceptedPayerPlans.push({ payerName, productLines });
    }

    if (acceptedPayerPlans.length === 0) {
      setError("Add at least one payer with product-line selections.");
      return;
    }

    setSaving(true);
    try {
      if (onSubmitAttestation) {
        await onSubmitAttestation({ acceptedPayerPlans });
      }
      setSuccess("Attestation draft saved.");
    } catch (err) {
      setError(err?.message || "Could not save attestation.");
    } finally {
      setSaving(false);
    }
  });

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Facility network attestation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Report participation at the product-line level for each payer. Requires
        CareVerify Manager. Payer name alone is not accepted.
      </Typography>

      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {validationHint ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationHint}
        </Alert>
      ) : null}

      <Stack component="form" onSubmit={onSubmit} spacing={3}>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Controller
              name={`payers.${index}.payerName`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Payer name"
                  fullWidth
                  disabled={disabled}
                  sx={{ mb: 1.5 }}
                />
              )}
            />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Product lines
            </Typography>
            <FormGroup>
              {PRODUCT_LINES.map((line) => (
                <Controller
                  key={line.key}
                  name={`payers.${index}.productLines.${line.key}`}
                  control={control}
                  render={({ field: f }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!f.value}
                          onChange={(e) => f.onChange(e.target.checked)}
                          disabled={disabled}
                        />
                      }
                      label={line.label}
                    />
                  )}
                />
              ))}
            </FormGroup>
          </Box>
        ))}

        <Stack direction="row" spacing={1}>
          <Button
            type="button"
            disabled={disabled || saving}
            onClick={() =>
              append({ payerName: "", productLines: emptyProductLines() })
            }
          >
            Add payer
          </Button>
          <LoadingButton type="submit" loading={saving} disabled={disabled}>
            Save attestation
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default FacilityAttestationPage;
